import { createContext, useContext, useRef, useState, useCallback } from "react";

const AmbienceContext = createContext(null);

/**
 * Single-track ambience — guzheng only.
 * `intensity` (0..1, default 1) is scaled by section-level scrolls (e.g. ArrivalJourney)
 * for gentle in/out swells. Volume default is 0.3 (soft).
 */
export const AmbienceProvider = ({ children }) => {
  const audioRef = useRef(null);
  const volumeRef = useRef(0.1);
  const intensityRef = useRef(1);
  const [playing, setPlaying] = useState(false);
  const [volume, setVolumeState] = useState(0.1);

  const applyVolume = useCallback(() => {
    if (audioRef.current) {
      audioRef.current.volume = Math.max(0, Math.min(1, volumeRef.current * intensityRef.current));
    }
  }, []);

  const start = useCallback(() => {
    applyVolume();
    audioRef.current?.play().then(() => setPlaying(true)).catch(() => {});
  }, [applyVolume]);

  const pause = useCallback(() => {
    audioRef.current?.pause();
    setPlaying(false);
  }, []);

  const toggle = useCallback(() => (playing ? pause() : start()), [playing, pause, start]);

  const setVolume = useCallback(
    (v) => {
      volumeRef.current = v;
      setVolumeState(v);
      applyVolume();
    },
    [applyVolume]
  );

  const setIntensity = useCallback(
    (f) => {
      intensityRef.current = f;
      applyVolume();
    },
    [applyVolume]
  );

  // Kept for backwards-compat — treatment layer is disabled; this is a no-op so callers
  // (e.g. SignatureRituals) don't crash.
  const setTreatmentIntensity = useCallback(() => {}, []);

  return (
    <AmbienceContext.Provider
      value={{ playing, volume, start, pause, toggle, setVolume, setIntensity, setTreatmentIntensity }}
    >
      <audio ref={audioRef} src="/audio/ambient.mp3" loop preload="auto" />
      {children}
    </AmbienceContext.Provider>
  );
};

export const useAmbience = () => useContext(AmbienceContext);
